import { motion, useReducedMotion } from 'motion/react'

// Branded logo header: the Zota wordmark centred in a light-blue gradient
// panel with soft blurred blobs behind it. Shared by the login and verify
// screens so they stay in sync.
//
// On the verify screen (animateEnvelope), the wordmark gives way to a looping
// envelope animation: a brand-blue envelope slides up and pushes the logo out
// of frame, opens, holds, then slides away as the logo slides back in.

const BLOBS = [
  { src: 'logo-blob-3.svg', w: 285, h: 276, x: 6, y: 40 },
  { src: 'logo-blob-2.svg', w: 289, h: 252, x: 234, y: -34 },
  { src: 'logo-blob-1.svg', w: 268, h: 242, x: 362, y: 38 },
  { src: 'logo-blob-4.svg', w: 268, h: 242, x: 96, y: 129 },
]

const panelStyle: React.CSSProperties = {
  position: 'relative',
  overflow: 'hidden',
  background: 'var(--color-surface-base)',
  borderRadius: '20px',
  padding: 'var(--spacing-4) 0',
  marginBottom: 'var(--spacing-8)',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

export function LogoHeader({ animateEnvelope = false }: { animateEnvelope?: boolean }) {
  const shouldReduceMotion = useReducedMotion()

  return (
    <div style={panelStyle}>
      {/* Decorative gradient blobs behind the logo */}
      {BLOBS.map((b) => (
        <img
          key={b.src}
          src={`${import.meta.env.BASE_URL}${b.src}`}
          alt=""
          aria-hidden="true"
          style={{
            position: 'absolute',
            width: `${b.w}px`,
            height: `${b.h}px`,
            left: `${b.x}px`,
            top: `${b.y}px`,
            transform: 'translate(-50%, -50%)',
            pointerEvents: 'none',
            userSelect: 'none',
          }}
        />
      ))}

      {animateEnvelope && !shouldReduceMotion ? <EnvelopeIntro /> : <Wordmark />}
    </div>
  )
}

function Wordmark() {
  return (
    <img
      src={`${import.meta.env.BASE_URL}zota-logo.png`}
      alt="Zota"
      style={{ position: 'relative', width: '80px', height: 'auto', display: 'block' }}
    />
  )
}

// ---- Envelope intro animation -------------------------------------------

const LOOP = 5.5 // seconds, looping

// Subtle ease-in shared by every moving part: each movement starts gently and settles softly.
const EASE: [number, number, number, number] = [0.5, 0, 0.6, 1]

const layer: React.CSSProperties = {
  position: 'absolute',
  inset: 0,
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'center',
}

function EnvelopeIntro() {
  const ease = EASE

  return (
    <div style={{ position: 'relative', width: '110px', height: '80px' }}>
      {/* Zota wordmark: holds centred, gets pushed up out of frame, then slides back in from below */}
      <motion.div
        style={layer}
        animate={{
          y: [0, 0, -110, -130, 130, 130, 0, 0],
          opacity: [1, 1, 1, 0, 0, 1, 1, 1],
        }}
        transition={{
          duration: LOOP,
          repeat: Infinity,
          ease,
          times: [0, 0.2, 0.291, 0.327, 0.709, 0.764, 0.909, 1],
        }}
      >
        <img
          src={`${import.meta.env.BASE_URL}zota-logo.png`}
          alt="Zota"
          style={{ width: '80px', height: 'auto', display: 'block' }}
        />
      </motion.div>

      {/* Envelope: slides up from below, opens, holds, then slides out the top.
          It only ever moves upward on screen — the reset to the bottom happens
          off-screen at the loop seam, so it never appears to fall back down. */}
      <motion.div
        style={layer}
        animate={{
          y: [160, 160, 0, 0, -160, -160],
        }}
        transition={{
          duration: LOOP,
          repeat: Infinity,
          ease,
          times: [0, 0.2, 0.364, 0.673, 0.818, 1],
        }}
      >
        <Envelope />
      </motion.div>
    </div>
  )
}

// Flap as a triangle: sharp base corners, rounded apex (the point) via a small curve.
// Closed = apex down; open = apex up. Same command structure so framer can morph the `d`.
const FLAP_CLOSED = 'M16,52 L104,52 L67,87 Q60,94 53,87 Z'
const FLAP_OPEN = 'M16,52 L104,52 L67,15 Q60,8 53,15 Z'

function Envelope() {
  return (
    <svg
      width="96"
      viewBox="0 0 120 140"
      fill="none"
      style={{ overflow: 'visible', display: 'block' }}
      aria-hidden="true"
    >
      <defs>
        <linearGradient id="env-body" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#2f7bee" />
          <stop offset="1" stopColor="#025de0" />
        </linearGradient>
        <linearGradient id="env-flap" x1="0" y1="0" x2="0" y2="1">
          <stop offset="0" stopColor="#0a55cc" />
          <stop offset="1" stopColor="#0246ab" />
        </linearGradient>
        {/* Rounds the envelope body: square top corners, lightly rounded bottom corners */}
        <clipPath id="env-round">
          <path d="M16,52 L104,52 L104,110 A6,6 0 0 1 98,116 L22,116 A6,6 0 0 1 16,110 Z" />
        </clipPath>
      </defs>

      {/* Soft contact shadow */}
      <ellipse cx="60" cy="122" rx="46" ry="6" fill="#0246ab" opacity="0.16" />

      {/* Body, clipped to rounded corners */}
      <g clipPath="url(#env-round)">
        {/* Inside back wall of the envelope (revealed when the flap opens) — deep blue, in shadow, so it reads as empty */}
        <rect x="16" y="52" width="88" height="64" fill="#0a3a82" />

        {/* Front folds meeting at the centre point (60,76) */}
        <path d="M16,116 L60,76 L104,116 Z" fill="url(#env-body)" />
        <path d="M16,52 L16,116 L60,76 Z" fill="#1466d9" />
        <path d="M104,52 L104,116 L60,76 Z" fill="#0f5ed0" />
      </g>

      {/* Top flap: animates from closed (apex down) to open (apex up) */}
      <motion.path
        fill="url(#env-flap)"
        animate={{ d: [FLAP_CLOSED, FLAP_CLOSED, FLAP_OPEN, FLAP_OPEN, FLAP_CLOSED, FLAP_CLOSED] }}
        transition={{
          duration: LOOP,
          repeat: Infinity,
          ease: EASE,
          times: [0, 0.364, 0.473, 0.818, 0.873, 1],
        }}
      />
    </svg>
  )
}
